/**
 * Copied shared contract in Next/Vue: only a shortlink handoff enables sales calls.
 * The marker is a scheduling hint; the server verifies the HttpOnly visitor cookie.
 */
const HANDOFF_TOKEN = /^(\d{1,20})~(\d{13})~[A-Za-z0-9_-]{32}$/;

export function createSalesAttributionClient(getAnonymousId) {
  const key = "nz_sales_bind_pending";
  const handoff = "nz_sales_pending";
  let flight = null;
  let flightMode = "";
  let lastAttempt = null;

  function readPending() {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || "null");
      if (
        !value ||
        !/^\d+$/.test(String(value.rootTouchId)) ||
        !Number.isFinite(value.expiresAt) ||
        value.expiresAt <= Date.now()
      ) {
        window.localStorage.removeItem(key);
        return null;
      }
      return value;
    } catch {
      return null;
    }
  }

  function captureHandoff() {
    try {
      const raw = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(handoff + "="))
        ?.slice(handoff.length + 1);
      if (!raw) return;
      // `<root>.<expiresMs>[.<handoffToken>]` — 토큰은 카카오 로그인이 다른 브라우저로 돌아올 때 state 로 왕복시킨다.
      const match = /^(\d+)\.(\d{13})(?:\.([^.]+))?$/.exec(raw);
      if (match && Number(match[2]) > Date.now()) {
        const previous = readPending();
        const token =
          match[3] && HANDOFF_TOKEN.test(match[3]) && match[3].startsWith(match[1] + "~")
            ? match[3]
            : null;
        if (!previous || previous.rootTouchId !== match[1]) {
          window.localStorage.setItem(
            key,
            JSON.stringify({
              rootTouchId: match[1],
              expiresAt: Number(match[2]),
              ...(token ? { handoffToken: token } : {}),
            }),
          );
        } else if (token && !previous.handoffToken) {
          window.localStorage.setItem(
            key,
            JSON.stringify({ ...previous, handoffToken: token }),
          );
        }
      }
      // Consume only the readable handoff, never the HttpOnly visitor or analytics ID.
      document.cookie = handoff + "=; Path=/; Max-Age=0; SameSite=Lax";
      document.cookie =
        handoff +
        "=; Domain=.naezipscan.com; Path=/; Max-Age=0; SameSite=Lax; Secure";
    } catch {
      /* Storage restrictions must not block the customer flow. */
    }
  }

  function clearIfCurrent(rootTouchId) {
    if (readPending()?.rootTouchId === rootTouchId)
      window.localStorage.removeItem(key);
  }

  /** 카카오 authorize `state` 에 실을 토큰. 살아있는 pending 이 있을 때만 반환한다. */
  function pendingHandoffToken() {
    try {
      captureHandoff();
      const pending = readPending();
      return pending && HANDOFF_TOKEN.test(pending.handoffToken || "")
        ? pending.handoffToken
        : null;
    } catch {
      return null;
    }
  }

  /**
   * 로그인 콜백이 다른 브라우저(앱)에서 열렸을 때 state 로 돌아온 토큰을 pending 으로 복원한다.
   * 이미 같은 root 가 있으면 토큰만 보강하고, 다른 root 가 살아 있으면 그쪽(먼저 클릭)을 유지한다.
   */
  function importHandoff(token) {
    try {
      const match = HANDOFF_TOKEN.exec(token || "");
      if (!match || Number(match[2]) <= Date.now()) return false;
      const previous = readPending();
      if (previous && previous.rootTouchId !== match[1]) return false;
      window.localStorage.setItem(
        key,
        JSON.stringify({
          rootTouchId: match[1],
          expiresAt: previous ? previous.expiresAt : Number(match[2]),
          ...(previous && previous.identityCheckedFor
            ? { identityCheckedFor: previous.identityCheckedFor }
            : {}),
          handoffToken: token,
        }),
      );
      return true;
    } catch {
      return false;
    }
  }

  async function run(accessToken, fetchImpl, identifyOnly) {
    captureHandoff();
    const pending = readPending();
    if (!pending || (!identifyOnly && !accessToken)) return false;
    const mode = accessToken ? "bind" : "identify";
    if (flight) {
      if (flightMode === mode) return flight;
      await flight;
      return run(accessToken, fetchImpl, identifyOnly);
    }
    const anonymousId = getAnonymousId();
    const validAnonymous =
      typeof anonymousId === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        anonymousId,
      );
    if (
      mode === "identify" &&
      (!validAnonymous || pending.identityCheckedFor === anonymousId)
    )
      return false;
    // At most one attempt per root/mode every 10 seconds, without retry timers.
    const attempt = pending.rootTouchId + ":" + mode;
    if (lastAttempt?.key === attempt && Date.now() - lastAttempt.at < 10000) return false;
    lastAttempt = { key: attempt, at: Date.now() };
    flightMode = mode;
    flight = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const requester = fetchImpl || window.fetch.bind(window);
        const response = await requester(
          "/next/api/sales-attribution/" + mode,
          {
            method: "POST",
            cache: "no-store",
          credentials: "same-origin",
          signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              ...(accessToken
                ? { Authorization: "Bearer " + accessToken }
                : {}),
            },
            body: JSON.stringify({
              ...(validAnonymous ? { anonymousId } : {}),
              // bind 에만 싣는다 — 서버는 방문자 쿠키가 없을 때만 토큰으로 귀속한다.
              ...(mode === "bind" && HANDOFF_TOKEN.test(pending.handoffToken || "")
                ? { handoffToken: pending.handoffToken }
                : {}),
            }),
          },
        );
        if (!response.ok) return false;
        const payload = await response.json();
        const terminal =
          payload.reason === "NO_VALID_TOUCH" ||
          payload.reason === "FIRST_TOUCH_LOCKED" ||
          payload.reason === "TOUCH_ALREADY_BOUND" ||
          payload.expired === true;
        if (terminal || payload.attributed === true) {
          clearIfCurrent(pending.rootTouchId);
          return payload.attributed === true;
        }
        if (
          mode === "identify" &&
          (payload.identified === true ||
            payload.reason === "IDENTITY_LOCKED" ||
            payload.reason === "IDENTITY_HANDOFF_EXPIRED") &&
          String(payload.rootTouchId) === pending.rootTouchId &&
          readPending()?.rootTouchId === pending.rootTouchId
        ) {
          const expiresAt = Date.parse(payload.expiresAt);
          if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
            // Server expiry is authoritative and can only shorten the local scheduling hint.
            window.localStorage.setItem(
              key,
              JSON.stringify({
                ...pending,
                expiresAt: Math.min(pending.expiresAt, expiresAt),
                identityCheckedFor: anonymousId,
              }),
            );
            return true;
          }
        }
        return false;
      } catch {
        return false;
      } finally { clearTimeout(timeout); }
    })();
    try {
      return await flight;
    } finally {
      flight = null;
      flightMode = "";
    }
  }

  return {
    bind: (accessToken, fetchImpl = undefined) =>
      run(accessToken, fetchImpl, false),
    initialize: (accessToken, fetchImpl = undefined) =>
      run(accessToken, fetchImpl, true),
    pendingHandoffToken,
    importHandoff,
  };
}
